import { useState } from 'react';
import { Icon } from '@iconify/react/dist/iconify.js';

const PendingChangesReview = ({ 
    pendingChanges, 
    isLoading, 
    onApprove, 
    onReject, 
    onConfirmBackendWork,
    selectedCustomer 
}) => {
    const [expandedChanges, setExpandedChanges] = useState(new Set());
    const [rejectingChangeId, setRejectingChangeId] = useState(null);
    const [rejectingChangeData, setRejectingChangeData] = useState(null);
    const [rejectionReason, setRejectionReason] = useState('');
    const [approvingChanges, setApprovingChanges] = useState(new Set());
    const [showConfirmModal, setShowConfirmModal] = useState(false);

    const toggleExpanded = (changeId) => {
        const newExpanded = new Set(expandedChanges);
        if (newExpanded.has(changeId)) {
            newExpanded.delete(changeId);
        } else {
            newExpanded.add(changeId);
        }
        setExpandedChanges(newExpanded);
    };

    const handleApprove = async (changeId, changeData) => {
        setApprovingChanges(prev => new Set([...prev, changeId]));
        try {
            await onApprove(changeId, changeData);
        } finally {
            setApprovingChanges(prev => {
                const newSet = new Set(prev);
                newSet.delete(changeId);
                return newSet;
            });
        }
    };

    const handleRejectClick = (changeId, changeData) => {
        setRejectingChangeId(changeId);
        setRejectingChangeData(changeData);
        setRejectionReason('');
    };

    const handleRejectConfirm = async () => {
        if (!rejectionReason.trim()) return;
        
        try {
            await onReject(rejectingChangeId, rejectionReason, rejectingChangeData);
            setRejectingChangeId(null);
            setRejectingChangeData(null);
            setRejectionReason('');
        } catch (error) {
            console.error('Error rejecting change:', error);
        }
    };

    const handleRejectCancel = () => {
        setRejectingChangeId(null);
        setRejectingChangeData(null);
        setRejectionReason('');
    };

    const formatTimestamp = (timestamp) => {
        return new Date(timestamp).toLocaleString();
    };

    const getChangeTypeIcon = (changeType, changeAction) => {
        if (changeType === 'policy') {
            return changeAction === 'create' ? 'material-symbols:policy-rounded' : 
                   changeAction === 'delete' ? 'material-symbols:policy-remove' : 'material-symbols:policy-edit';
        } else if (changeType === 'parent') {
            return changeAction === 'create' ? 'mdi:folder-plus' : 
                   changeAction === 'delete' ? 'mdi:folder-remove' : 'mdi:folder-edit';
        } else {
            return changeAction === 'create' ? 'mdi:package-variant-plus' : 
                   changeAction === 'delete' ? 'mdi:package-variant-remove' : 'mdi:package-variant';
        }
    };

    const getChangeTypeColor = (changeAction) => {
        return changeAction === 'create' ? 'text-green-600' : 
               changeAction === 'delete' ? 'text-red-600' : 'text-blue-600';
    };

    const getStatusColor = (status) => {
        return status === 'approved' ? 'text-green-600' : 
               status === 'rejected' ? 'text-red-600' : 'text-orange-600';
    };

    const renderDataComparison = (originalData, modifiedData, newData, changeType, changeAction) => {
        // Handle creation requests (newData exists)
        if (changeAction === 'create' && newData) {
            if (changeType === 'policy') {
                return (
                    <div className="p-4 tag-primary">
                        <h4 className="font-semibold text-primary mb-3 flex items-center gap-2">
                            <Icon icon="mdi:plus-circle" className="text-lg" />
                            New Policy Data
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                            <div><span className="font-medium">Policy Type:</span> {newData?.policy_type || 'N/A'}</div>
                            <div className="md:col-span-2"><span className="font-medium">Policy Text:</span> {newData?.policy_text || 'N/A'}</div>
                        </div>
                    </div>
                );
            } else if (changeType === 'parent') {
                return (
                    <div className="p-4 tag-primary">
                        <h4 className="font-semibold text-primary mb-3 flex items-center gap-2">
                            <Icon icon="mdi:plus-circle" className="text-lg" />
                            New Parent Category Data
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                            <div><span className="font-medium">Category Name:</span> {newData?.parentCategory || 'N/A'}</div>
                            <div><span className="font-medium">Typical Uses:</span> {newData?.typicalUses || 'N/A'}</div>
                            <div><span className="font-medium">Typical Flow:</span> {newData?.typicalFlow || 'N/A'}</div>
                            <div><span className="font-medium">Defaults:</span> {newData?.defaults || 'N/A'}</div>
                            <div><span className="font-medium">Qualifying Questions:</span> {newData?.qualifyingQuestions || 'N/A'}</div>
                            <div><span className="font-medium">Special Notes:</span> {newData?.specialNotes || 'N/A'}</div>
                            <div className="md:col-span-2"><span className="font-medium">Pricing Considerations:</span> {newData?.pricingConsiderations || 'N/A'}</div>
                        </div>
                    </div>
                );
            } else {
                return (
                    <div className="p-4 tag-secondary">
                        <h4 className="font-semibold text-secondary mb-3 flex items-center gap-2">
                            <Icon icon="mdi:plus-circle" className="text-lg" />
                            New Child Item Data
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                            <div><span className="font-medium">Item Name:</span> {newData?.childCategory || 'N/A'}</div>
                            <div><span className="font-medium">Model:</span> {newData?.model || 'N/A'}</div>
                            <div><span className="font-medium">Brand:</span> {newData?.brand || 'N/A'}</div>
                            <div><span className="font-medium">Customer Reference:</span> {newData?.customerReference || 'N/A'}</div>
                            <div><span className="font-medium">Common Synonyms:</span> {newData?.commonSynonyms || 'N/A'}</div>
                            <div><span className="font-medium">Key Selling Points:</span> {newData?.keySellingPoints || 'N/A'}</div>
                            <div className="md:col-span-2"><span className="font-medium">Description:</span> {newData?.description || 'N/A'}</div>
                            <div className="md:col-span-2"><span className="font-medium">Pricing:</span> {newData?.pricing || 'N/A'}</div>
                        </div>
                    </div>
                );
            }
        }
        
        // Handle modification requests (originalData and modifiedData exist)
        if (changeType === 'policy') {
            return (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div className="p-3 tag-primary">
                        <h4 className="font-semibold text-primary mb-2 flex items-center gap-1">
                            <Icon icon="mdi:database" className="text-lg" />
                            Original Data
                        </h4>
                        <div className="space-y-1 text-sm">
                            <div><span className="font-medium">Policy Type:</span> {originalData?.policy_type || 'N/A'}</div>
                            <div className="mt-2"><span className="font-medium">Policy Text:</span> <div className="mt-1 whitespace-pre-wrap">{originalData?.policy_text || 'N/A'}</div></div>
                        </div>
                    </div>
                    <div className="p-3 tag-secondary">
                        <h4 className="font-semibold text-secondary mb-2 flex items-center gap-2">
                            <Icon icon="mdi:pencil" className="text-lg" />
                            Modified Data
                        </h4>
                        <div className="space-y-1 text-sm">
                            <div><span className="font-medium">Policy Type:</span> {modifiedData?.policy_type || 'N/A'}</div>
                            <div className="mt-2"><span className="font-medium">Policy Text:</span> <div className="mt-1 whitespace-pre-wrap">{modifiedData?.policy_text || 'N/A'}</div></div>
                        </div>
                    </div>
                </div>
            );
        } else if (changeType === 'parent') {
            return (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div className="p-3 tag-primary">
                        <h4 className="font-semibold text-primary mb-2 flex items-center gap-1">
                            <Icon icon="mdi:database" className="text-lg" />
                            Original Data
                        </h4>
                        <div className="space-y-1 text-sm">
                            <div><span className="font-medium">Category:</span> {originalData?.parentCategory || 'N/A'}</div>
                            <div><span className="font-medium">Typical Uses:</span> {originalData?.typicalUses || 'N/A'}</div>
                            <div><span className="font-medium">Description:</span> {originalData?.description || 'N/A'}</div>
                        </div>
                    </div>
                    <div className="p-3 tag-secondary">
                        <h4 className="font-semibold text-secondary mb-2 flex items-center gap-2">
                            <Icon icon="mdi:pencil" className="text-lg" />
                            Modified Data
                        </h4>
                        <div className="space-y-1 text-sm">
                            <div><span className="font-medium">Category:</span> {modifiedData?.parentCategory || 'N/A'}</div>
                            <div><span className="font-medium">Typical Uses:</span> {modifiedData?.typicalUses || 'N/A'}</div>
                            <div><span className="font-medium">Description:</span> {modifiedData?.description || 'N/A'}</div>
                        </div>
                    </div>
                </div>
            );
        } else {
            return (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div className="p-3 tag-primary">
                        <h4 className="font-semibold text-primary mb-2 flex items-center gap-2">
                            <Icon icon="mdi:database" className="text-lg" />
                            Original Data
                        </h4>
                        <div className="space-y-1 text-sm">
                            <div><span className="font-medium">Category:</span> {originalData?.childCategory || 'N/A'}</div>
                            <div><span className="font-medium">Description:</span> {originalData?.description || 'N/A'}</div>
                            <div><span className="font-medium">Notes:</span> {originalData?.notes || 'N/A'}</div>
                        </div>
                    </div>
                    <div className="p-3 tag-secondary">
                        <h4 className="font-semibold text-secondary mb-2 flex items-center gap-2">
                            <Icon icon="mdi:pencil" className="text-lg" />
                            Modified Data
                        </h4>
                        <div className="space-y-1 text-sm">
                            <div><span className="font-medium">Category:</span> {modifiedData?.childCategory || 'N/A'}</div>
                            <div><span className="font-medium">Description:</span> {modifiedData?.description || 'N/A'}</div>
                            <div><span className="font-medium">Notes:</span> {modifiedData?.notes || 'N/A'}</div>
                        </div>
                    </div>
                </div>
            );
        }
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <Icon icon="mdi:loading" className="text-4xl text-primary animate-spin mx-auto mb-4" />
                    <p className="text-gray-600">Loading pending changes...</p>
                </div>
            </div>
        );
    }

    // Ensure pendingChanges is always an array
    const safePendingChanges = Array.isArray(pendingChanges) ? pendingChanges : [];
    const approvedChanges = safePendingChanges.filter(change => change.changeStatus === 'approved');
    const hasApprovedChanges = approvedChanges.length > 0;

    return (
        <div className="space-y-4">
            {/* Summary */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="card-container p-2 sm:p-3 rounded-md">
                    <div className="flex items-center gap-2">
                        <Icon icon="material-symbols:pending-actions" className="text-2xl text-orange-600" />
                        <div>
                            <p className="text-sm text-font-secondary">Pending Reviews</p>
                            <p className="text-xl font-bold text-orange-600">
                                {safePendingChanges.filter(c => c.changeStatus === 'pending').length}
                            </p>
                        </div>
                    </div>
                </div>
                <div className="card-container p-2 sm:p-3 rounded-md">
                    <div className="flex items-center gap-2">
                        <Icon icon="material-symbols:check-circle" className="text-2xl text-green-600" />
                        <div>
                            <p className="text-sm text-font-secondary">Approved</p>
                            <p className="text-xl font-bold text-green-600">
                                {safePendingChanges.filter(c => c.changeStatus === 'approved').length}
                            </p>
                        </div>
                    </div>
                </div>
                <div className="card-container p-2 sm:p-3 rounded-md">
                    <div className="flex items-center gap-2">
                        <Icon icon="material-symbols:cancel" className="text-2xl text-red-600" />
                        <div>
                            <p className="text-sm text-font-secondary">Rejected</p>
                            <p className="text-xl font-bold text-red-600">
                                {safePendingChanges.filter(c => c.changeStatus === 'rejected').length}
                            </p>
                        </div>
                    </div>
                </div>
                <div className="card-container p-2 sm:p-3 rounded-md">
                    <div className="flex items-center gap-2">
                        <Icon icon="mdi:plus-circle" className="text-2xl text-blue-600" />
                        <div>
                            <p className="text-sm text-font-secondary">New Equipment</p>
                            <p className="text-xl font-bold text-blue-600">
                                {safePendingChanges.filter(c => c.changeAction === 'create').length}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Backend Work Confirmation */}
            {hasApprovedChanges && (
                <div className="p-2 sm:p-3 tag-green">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <Icon icon="material-symbols:verified" className="text-2xl text-green-600" />
                            <div>
                                <p className="font-medium text-green-800">Ready for Backend Confirmation</p>
                                <p className="text-sm text-green-600">
                                    {approvedChanges.length} approved change(s) ready for backend implementation
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={() => setShowConfirmModal(true)}
                            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                        >
                            <Icon icon="material-symbols:check-circle" className="text-lg" />
                            Confirm Backend Work Complete
                        </button>
                    </div>
                </div>
            )}

            {/* Changes List */}
            {safePendingChanges.length === 0 ? (
                <div className="card-container p-2 sm:p-3 rounded-md text-center">
                    <Icon icon="material-symbols:checklist" className="text-4xl text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-font-primary mb-2">No Pending Changes</h3>
                    <p className="text-font-secondary">
                        No equipment changes are currently pending review for {selectedCustomer?.name}.
                    </p>
                </div>
            ) : (
                <div className="space-y-3">
                    {safePendingChanges.map((change) => (
                        <div key={change.id} className="button-container p-3 rounded-md overflow-hidden">
                            <div className="">
                                <div className="flex  items-start justify-between">
                                    <div className="flex flex-col items-start gap-3 flex-1">
                                        <div className="flex items-center gap-1">
                                            <Icon 
                                                icon={getChangeTypeIcon(change.changeType, change.changeAction)} 
                                                className={`text-2xl ${getChangeTypeColor(change.changeAction)} mt-1`} 
                                            />
                                            <div className="flex items-center gap-3">
                                                <h3 className="font-semibold text-font-primary">
                                                    {change.changeType === 'policy' ? 'Policy' : 
                                                     change.changeType === 'parent' ? 'Category' : 'Equipment Item'} - 
                                                    {change.changeAction === 'create' ? ' New Creation' : ' Modification'}
                                                </h3>
                                                <span className={`p-1.5 rounded-md text-xs font-semibold tag-orange `}>
                                                    {change.changeStatus.charAt(0).toUpperCase() + change.changeStatus.slice(1)}
                                                </span>
                                                {change.changeAction === 'create' && (
                                                    <span className="p-1.5 rounded-md text-xs font-semibold tag-secondary">
                                                        New
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex-1">
                                            <div className="space-y-1 text-sm text-font-secondary">
                                                <div className="font-semibold text-font-primary">
                                                    <span className="font-medium text-font-secondary">Changed by:</span> {change.changedBy}
                                                </div>
                                                <div className="font-semibold text-font-primary">
                                                    <span className="font-medium text-font-secondary">Changed at:</span> {formatTimestamp(change.changedAt)}
                                                </div>
                                                {change.changeType === 'parent' && (
                                                    <div className="font-semibold text-font-primary">
                                                        <span className="font-medium text-font-secondary">Category ID:</span> {change.changeAction === 'create' ? 'New' : change.parentEqpId}
                                                    </div>
                                                )}
                                                {change.changeType === 'child' && (
                                                    <div className="font-semibold text-font-primary">
                                                        <span className="font-medium text-font-secondary">Parent ID:</span> {change.parentEqpId} | 
                                                        <span className="font-medium text-font-secondary"> Child ID:</span> {change.changeAction === 'create' ? 'New' : change.childEqpId}
                                                    </div>
                                                )}
                                                {change.changeType === 'policy' && (
                                                    <div className="font-semibold text-font-primary">
                                                        <span className="font-medium text-font-secondary">Policy ID:</span> {change.changeAction === 'create' ? 'New' : (change.policyId || 'N/A')}
                                                    </div>
                                                )}
                                                {change.rejectionReason && (
                                                    <div className="text-red-600 font-semibold text-font-primary">
                                                        <span className="font-medium text-font-secondary">Rejection Reason:</span> {change.rejectionReason}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => toggleExpanded(change.id)}
                                            className="p-2 rounded-lg hover:bg-bgAccent transition-colors"
                                            title="View details"
                                        >
                                            <Icon 
                                                icon={expandedChanges.has(change.id) ? 'mdi:chevron-up' : 'mdi:chevron-down'} 
                                                className="text-xl text-accent" 
                                            />
                                        </button>
                                        {change.changeStatus === 'pending' && (
                                            <>
                                                <button
                                                    onClick={() => handleApprove(change.id, change)}
                                                    disabled={approvingChanges.has(change.id)}
                                                    className="flex items-center gap-1 px-3 py-1 tag-green text-sm rounded-md hover:bg-green-700 transition-colors disabled:opacity-50"
                                                >
                                                    {approvingChanges.has(change.id) ? (
                                                        <Icon icon="mdi:loading" className="text-lg animate-spin text-green-500" />
                                                    ) : (
                                                        <Icon icon="material-symbols:check" className="text-lg text-green-500" />
                                                    )}
                                                    <span className="text-font-primary">Approve</span>
                                                </button>
                                                <button
                                                    onClick={() => handleRejectClick(change.id, change)}
                                                    className="flex items-center gap-1 px-3 py-1 tag-red text-sm rounded-md hover:bg-red-700 transition-colors"
                                                >
                                                    <Icon icon="material-symbols:close" className="text-lg text-red-500" />
                                                    <span className="text-font-primary">Reject</span>
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                            
                            {/* Expanded Details */}
                            {expandedChanges.has(change.id) && (
                                <div className="mt-3 border-t-2 border-primary-50 pt-3">
                                    <h4 className="font-semibold text-font-primary mb-3">Change Details</h4>
                                    {renderDataComparison(change.originalData, change.modifiedData, change.newData, change.changeType, change.changeAction)}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* Rejection Modal */}
            {rejectingChangeId && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="card-container rounded-md p-6 w-full max-w-md mx-4">
                        <h3 className="text-lg font-medium text-error mb-4">Reject Change</h3>
                        <p className="text-sm text-font-secondary mb-4">
                            Please provide a reason for rejecting this change:
                        </p>
                        <textarea
                            value={rejectionReason}
                            onChange={(e) => setRejectionReason(e.target.value)}
                            placeholder="Rejection reason..."
                            className="w-full p-3 bg-background border border-border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-error"
                            rows="3"
                        />
                        <div className="flex justify-end gap-2 mt-4">
                            <button
                                onClick={handleRejectCancel}
                                className="px-4 py-2 text-font-secondary hover:bg-bgAccent rounded-lg transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleRejectConfirm}
                                disabled={!rejectionReason.trim()}
                                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                            >
                                Reject Change
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Backend Confirmation Modal */}
            {showConfirmModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
                        <h3 className="text-lg font-medium text-gray-900 mb-4">Confirm Backend Work</h3>
                        <p className="text-sm text-gray-600 mb-4">
                            Are you sure you want to confirm that all approved changes have been implemented in the backend? 
                            This will clear all pending changes and refresh the equipment data from PostgreSQL.
                        </p>
                        <div className="bg-yellow-50 border border-yellow-200 rounded p-3 mb-4">
                            <p className="text-yellow-800 text-sm">
                                <strong>Warning:</strong> This action cannot be undone. Make sure all approved changes 
                                have been properly implemented in the PostgreSQL database.
                            </p>
                        </div>
                        <div className="flex justify-end gap-2">
                            <button
                                onClick={() => setShowConfirmModal(false)}
                                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => {
                                    onConfirmBackendWork();
                                    setShowConfirmModal(false);
                                }}
                                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                            >
                                Confirm Backend Work Complete
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PendingChangesReview;
